import React from "react"
import { useTranslation } from "react-i18next"
import {
  ProductAssociationApplyPopup,
  type ProductAssociationLabels,
  type ProductAssociationRequest,
} from "../../../components/Associations/ProductAssociationApplyPopup"
import { EAdditionalApplyTargetType } from "../../../Enum/EAdditionalApplyTargetType"
import type { ApplyAdditionalRequest } from "../../../hooks/Addtional/useApplyAdditional"

interface AdditionalApplyPopupProps {
  title: string
  actionLabel: string
  helpTitle: string
  helpDescription: string
  helpBullets: string[]
  helpKey: string
  onClose: () => void
  onConfirm: (request: ApplyAdditionalRequest) => Promise<void>
}

export const AdditionalApplyPopup: React.FC<AdditionalApplyPopupProps> = ({ onConfirm, ...props }) => {
  const { t } = useTranslation("addtional")

  const labels: ProductAssociationLabels = {
    targetLegend: t("apply_target_legend"),
    allProducts: t("apply_target_all_products"),
    selectedProducts: t("apply_target_selected_products"),
    allCatalogItems: t("apply_target_all_catalogItems"),
    selectedCatalogItems: t("apply_target_selected_catalogItems"),
    allCombos: t("apply_target_all_combos"),
    selectedCombos: t("apply_target_selected_combos"),
    selectProductsAction: t("apply_select_products_action"),
    selectCatalogItemsAction: t("apply_select_catalogItems_action"),
    selectCombosAction: t("apply_select_combos_action"),
    selectedProductsCount: t("apply_selected_products"),
    selectedCatalogItemsCount: t("apply_selected_catalogItems"),
    selectedCombosCount: t("apply_selected_combos"),
    selectedProductsTitle: t("apply_target_selected_products"),
    selectedCatalogItemsTitle: t("apply_target_selected_catalogItems"),
    selectedCombosTitle: t("apply_target_selected_combos"),
    noSelectedProducts: t("apply_no_selected_products"),
    noSelectedCatalogItems: t("apply_no_selected_catalogItems"),
    noSelectedCombos: t("apply_no_selected_combos"),
  }

  const handleConfirm = (request: ProductAssociationRequest) =>
    onConfirm({ ...request, targetType: request.targetType as EAdditionalApplyTargetType })

  return (
    <ProductAssociationApplyPopup
      {...props}
      labels={labels}
      targetValues={{
        allProducts: EAdditionalApplyTargetType.AllProducts,
        selectedProducts: EAdditionalApplyTargetType.SelectedProducts,
        allCatalogItems: EAdditionalApplyTargetType.AllCatalogItems,
        selectedCatalogItems: EAdditionalApplyTargetType.SelectedCatalogItems,
        allCombos: EAdditionalApplyTargetType.AllCombos,
        selectedCombos: EAdditionalApplyTargetType.SelectedCombos,
      }}
      interactionKeys={{
        closeDescriptionKey: "interactionDescriptions:pages.products.addtional.additionalApplyPopup.button.onClose",
        targetDescriptionKey:
          "interactionDescriptions:pages.products.addtional.additionalApplyPopup.select.applyTargetLegend",
        confirmDescriptionKey: "interactionDescriptions:pages.products.addtional.additionalApplyPopup.button.actionLabel",
        addProductDescriptionKey: "interactionDescriptions:pages.products.addtional.additionalApplyPopup.button.addProduct",
        removeProductDescriptionKey:
          "interactionDescriptions:pages.products.addtional.additionalApplyPopup.button.removeProduct",
        addCatalogItemDescriptionKey:
          "interactionDescriptions:pages.products.addtional.additionalApplyPopup.button.addCatalogItem",
        removeCatalogItemDescriptionKey:
          "interactionDescriptions:pages.products.addtional.additionalApplyPopup.button.removeCatalogItem",
        addComboDescriptionKey: "interactionDescriptions:pages.products.addtional.additionalApplyPopup.button.addCombo",
        removeComboDescriptionKey:
          "interactionDescriptions:pages.products.addtional.additionalApplyPopup.button.removeCombo",
      }}
      onConfirm={handleConfirm}
    />
  )
}
